<template>
	<transition name="fade">
		<div v-if="serviceModalOpen" class="modal" tabindex="-1" ref="waModal">
			<div class="modal-dialog modal-md">
				<div class="modal-content px-2">
                    <div class="modal-header">
                        <h5 v-if="modalHeaderUsesHtml" class="modal-title" v-html="modalHeader"></h5>
                        <h5 v-else class="modal-title">{{ modalHeader }}</h5>
                        <button type="button" class="btn-close" aria-label="Close" @click="dismissModal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="row">
                            <template v-if="hasBothServices">
                                <div v-for="service in servicesForImages" :key="service.code" class="col-6 mb-2">
                                    <img class="img-fluid h-100 rounded" :src="serviceImageSrc(service)" :alt="service.name" />
                                </div>
                            </template>
                            <div v-else class="col-12 mb-2">
                                <img class="img-fluid h-100 rounded" :src="serviceImageSrc(services?.[0])" :alt="services?.[0]?.name" />
                            </div>
                            <div class="col-12 my-2">
                                <div v-html="getUpsellText()"></div>
                            </div>
                            <div v-if="hasBothServices" class="col-12 service-checkbox-list my-2">
                                <label
                                    v-for="service in servicesForImages"
                                    :key="service.code"
                                    class="service-checkbox"
                                    :for="`service-checkbox-${service.code}`"
                                >
                                    <input
                                        :id="`service-checkbox-${service.code}`"
                                        type="checkbox"
                                        class="service-checkbox__input"
                                        v-model="selectedServiceCodes"
                                        :value="service.code"
                                    />
                                    <span class="service-checkbox__box" aria-hidden="true"></span>
                                    <span class="service-checkbox__text">
                                        <strong>{{ service.name }}</strong>
                                        <span class="service-checkbox__price">
                                            &pound;{{ formatServiceRetailPrice(service) }}<template v-if="service.code === 'TYREPROTECTION'"> per tyre</template>
                                        </span>
                                    </span>
                                </label>
                            </div>
                            <div class="col-12 my-2">
                                <button class="btn btn-primary d-block w-100 green-bg" @click="confirmServiceUpsell">{{ getPrimaryButtonText() }}</button>
                                <button class="d-block w-100 btn btn-secondary mt-3" @click="this.$parent.handleServiceUpsell()">I'll take the risk</button>
                            </div>
                            <div v-if="hasTyreProtection" class="text-center">
                                <small><a class="text-underline" target="_blank" href="/tyre-protection">Terms and conditions apply.</a></small>
                            </div>
                        </div>
                    </div>
				</div>
			</div>
		</div>
	</transition>
</template>
<script>
    export default {
        props: ['services', 'service-modal-open', 'basket', 'tyre-admin-url', 'fuel-env'],
        emits: ['closeModal'],
        data() {
            return {
                selectedServiceCodes: []
            }
        },
        watch: {
            servicesForImages: {
                immediate: true,
                handler(services) {
                    this.selectedServiceCodes = services.map(service => service.code)
                }
            }
        },
        mounted() {
        },
        methods: {
            dismissModal() {
                this.$emit('closeModal')
                this.$parent.handleServiceUpsell()
            },
            formatServiceRetailPrice(service) {
                return parseFloat(service.retail).toFixed(2)
            },
            formatServicePrice(service) {
                return service.price_type == 'fixed'
                    ? service.retail
                    : parseFloat(service.retail * this.basket.total_tyres).toFixed(2)
            },
            defaultServiceHeader(service) {
                return `Would you like ${service.name} for only <b>&pound;${this.formatServicePrice(service)}</b>?`
            },
            hasWheelAlignmentCode(codes) {
                return codes.some(code => code === 'WHEELALIGNMENT' || code === 'WHEELALIGMENT')
            },
            isWheelAlignmentService(service) {
                return service?.code === 'WHEELALIGNMENT' || service?.code === 'WHEELALIGMENT'
            },
            serviceImageSrc(service) {
                if (!service?.modal_image_path) {
                    return ''
                }

                if (/^https?:\/\//.test(service.modal_image_path)) {
                    return service.modal_image_path
                }

                const base = this.serviceImageBaseUrl
                const path = service.modal_image_path.replace(/^\//, '')

                return `${base}${path}`
            },
            getServiceQuantity(service) {
                if (Boolean(Number(service?.priced_per_item)) || service?.price_type === 'per_tyre') {
                    return this.basket?.total_tyres || 1
                }

                return service.price_type === 'fixed' ? 1 : (this.basket?.total_tyres || 1)
            },
            getSelectedServices() {
                if (this.hasBothServices) {
                    return this.selectedServiceCodes
                        .map(code => (this.services ?? []).find(service => service.code === code))
                        .filter(Boolean)
                }

                const service = this.services?.[0]
                return service ? [service] : []
            },
            trackAcceptedUpsellServices(services) {
                const gtag = this.$gtag ?? this.$parent?.$gtag

                if (typeof gtag === 'undefined') {
                    console.log('gtag not loaded')
                    return
                }

                const serviceCodes = []

                for (const service of services) {
                    if (service?.code) {
                        serviceCodes.push(service.code)
                    }
                }

                if (serviceCodes.length === 0) {
                    return
                }

                gtag.event('service_modal', {
                    event_category: 'Checkout',
                    event_label: `Service Modal Upsell Accepted. Services added: ${serviceCodes.join(',')}`,
                    service_codes: serviceCodes.join(','),
                })
            },
            async confirmServiceUpsell() {
                const selectedServices = this.getSelectedServices()

                if (selectedServices.length === 0) {
                    this.$parent.handleServiceUpsell()
                    return
                }

                this.trackAcceptedUpsellServices(selectedServices)

                if (selectedServices.length === 1) {
                    const service = selectedServices[0]
                    this.$parent.handleServiceUpsell(service.code, this.getServiceQuantity(service))
                    return
                }

                const parent = this.$parent
                parent.serviceModalOpen = false
                this.$emit('closeModal')

                try {
                    for (const service of selectedServices) {
                        const basketFormData = new FormData()
                        basketFormData.append('st_code', service.code)
                        basketFormData.append('quantity', this.getServiceQuantity(service))

                        const response = await axios.post('/ajax/add_to_basket', basketFormData)
                        if (response.data.basket_updated === true) {
                            parent.updatedBasket = response.data.basket
                        }
                    }

                    parent.processOrder()
                } catch (error) {
                    console.error('There was an error!', error)
                }
            },
            getPrimaryButtonText() {
                if (this.hasBothServices) {
                    return 'Protect my tyres'
                }

                const service = this.services?.[0]
                if (!service) {
                    return 'Protect my tyres'
                }

                const perTyreSuffix = service.code === 'TYREPROTECTION' ? ' per tyre' : ''
                return `Add ${service.name} for £${this.formatServiceRetailPrice(service)}${perTyreSuffix}`
            },
            getUpsellText() {
                if (!this.hasBothServices) {
                    return this.services?.[0]?.modal_text ?? this.services?.[0]?.short_description ?? ''
                }

                // Default (Both services available)
                return `
                    <p>You're investing in new tyres, but they aren't fully protected yet. Add our essential extras to get the maximum lifespan and peace of mind from your purchase:</p>
                    <ul class="px-3 my-3">
                        <li class="fw-light mb-2"><strong>Tyre Protection:</strong> Your safety net. We'll cover the replacement cost if you suffer accidental damage, a kerb strike, or an unrepairable puncture.</li>
                        <li class="fw-light"><strong>Wheel Alignment:</strong> Your money saver. Ensures your car handles perfectly and your tyres wear evenly, adding thousands of miles to their lifespan.</li>
                    </ul>
                    <p class="mb-0">Drive away knowing you are completely covered. Select your cover:</p>
                `
            }
        },
        computed: {
            serviceImageBaseUrl() {
                const env = (this.fuelEnv ?? this.$parent?.fuelEnv ?? '').toLowerCase()

                if (env === 'production') {
                    return 'https://colewood-automotive.s3.eu-west-1.amazonaws.com/Images/' // S3 Bucket Path
                }

                const adminUrl = (this.tyreAdminUrl ?? this.$parent?.tyreAdminUrl ?? '').replace(/\/?$/, '/')

                if (!adminUrl || adminUrl.includes('/storage/')) {
                    return adminUrl
                }

                return `${adminUrl}storage/`
            },
            hasBothServices() {
                return this.hasTyreProtection && this.hasWheelAlignment
            },
            servicesForImages() {
                return (this.services ?? [])
                    .filter(service => service.code === 'TYREPROTECTION' || this.isWheelAlignmentService(service))
                    .sort((a, b) => (a.code === 'TYREPROTECTION' ? 0 : 1) - (b.code === 'TYREPROTECTION' ? 0 : 1))
            },
            serviceCodes() {
                return (this.services ?? []).map(service => service.code)
            },
            hasTyreProtection() {
                return this.serviceCodes.includes('TYREPROTECTION')
            },
            hasWheelAlignment() {
                return this.serviceCodes.includes('WHEELALIGNMENT')
            },
            modalHeaderUsesHtml() {
                return !this.hasTyreProtection && !this.hasWheelAlignment && (this.services?.length ?? 0) > 0
            },
            modalHeader() {
                if (this.hasTyreProtection && this.hasWheelAlignment) {
                    return 'Protect your new tyres before you check out!'
                }
                if (this.hasTyreProtection) {
                    return 'Add the ultimate safety net to your order'
                }
                if (this.hasWheelAlignment) {
                    return 'Get the absolute most out of your new tyres!'
                }
                const services = this.services ?? []
                if (services.length === 0) {
                    return 'Would you like to add a service to your order?'
                }
                return services.map(service => this.defaultServiceHeader(service)).join('<br>')
            }
        }
    }
</script>
<style lang="scss" scoped>
    .modal-title {
        font-size: 25px;
    }
    .error {
        color: red;
    }
    .btn-close {
        color: #D3D3D3;
    }
    .modal {
		background-color: rgba(0,0,0,.5);
	  	display: block;
	}
    .service-checkbox-list {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        margin-bottom: 0.5rem;
    }
    .service-checkbox {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        cursor: pointer;
        margin: 0;
    }
    .service-checkbox__input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
        pointer-events: none;
    }
    .service-checkbox__box {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        border-radius: 6px;
        border: 2px solid #d1d5db;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.15s ease, border-color 0.15s ease;
    }
    .service-checkbox__input:checked + .service-checkbox__box {
        background-color: #38B577;
        border-color: #38B577;
    }
    .service-checkbox__input:checked + .service-checkbox__box::after {
        content: '';
        width: 6px;
        height: 12px;
        border: solid #fff;
        border-width: 0 2.5px 2.5px 0;
        transform: rotate(45deg);
        margin-top: -2px;
    }
    .service-checkbox__text {
        line-height: 1.3;
    }
    .service-checkbox__price {
        font-size: 0.875rem;
        font-weight: 400;
        margin-left: 0.35rem;
    }
</style>
